import { defineStore } from 'pinia';
import { Experimentation } from '@nuxt-common/util/app/utils/experimentation';
import { addExperiment, getScaledExperimentIds } from '#shared/features/experimentation/Utils';
import { getURLParam } from '#shared/utils/Url';
import { commaSeparatedToArray } from '#shared/utils/StringUtil';
import {
  SETUP_CLIENT_SIDE_EXPERIMENTATION,
  SETUP_SERVER_SIDE_EXPERIMENTATION,
  SET_EXPERIMENT_ID,
  SET_EXPERIMENT_CLIENT_ID,
} from '#shared/types/actions';
import experiments from '#shared/features/experimentation/config';
import { GET_EXPERIMENTS, GET_EXPERIMENTS_IDS, GET_EXPERIMENTS_CLIENT_IDS } from '#shared/types/getters';
import { useNavKillswitch } from './navKillswitch';
import { useNavViewType } from './navViewType';
import { usePageData } from './pageData';

const recipieIdsFromUrl = (queryParam) => commaSeparatedToArray(getURLParam(queryParam));

const getClientRecipieIds = () => {
  const overrideRecipieIds = recipieIdsFromUrl('SSC').concat(recipieIdsFromUrl('CSC'));

  return Experimentation.getExpIds().concat(overrideRecipieIds);
};

export const useNavExperimentation = defineStore('navExperimentation', {
  state: () => ({
    ids: getScaledExperimentIds(), // filtered ids for ssr usage
    clientIds: [], // unfiltered all ids; available client side
  }),
  getters: {
    [GET_EXPERIMENTS](state) {
      const pageData = usePageData();
      const navView = useNavViewType();
      const { killswitches } = useNavKillswitch();
      const recipieOnly = pageData?.devMode?.queryParams?.recipieOnly;
      const idsMap = state.ids.reduce((map, cur) => {
        map[cur] = true;
        return map;
      }, {});

      return experiments.reduce((map, { ks, treatment, devices, scaled, arm }) => {
        if (map[ks]) return map;

        // Soft-scaled scenario
        if (scaled) {
          map[ks] = devices.includes(navView.viewType);
          return map;
        }

        // KS check is OFF
        if (recipieOnly) {
          map[ks] = Boolean(idsMap[treatment] && devices.includes(navView.viewType));
          return map;
        }

        // Two-Flags check
        const masterKsName = `master${ks.charAt(0).toUpperCase() + ks.slice(1)}`;
        const isMasterKsActive = Boolean(killswitches[masterKsName]);
        const isForceKsEnabled = Boolean(killswitches[ks] || (arm && killswitches[arm]));
        const isExpActive = Boolean(idsMap[treatment]);
        map[ks] = isMasterKsActive && (isForceKsEnabled || isExpActive) && devices.includes(navView.viewType);
        return map;
      }, {});
    },
    [GET_EXPERIMENTS_IDS](state) {
      return state.ids;
    },
    [GET_EXPERIMENTS_CLIENT_IDS](state) {
      return state.clientIds;
    },
  },
  actions: {
    [SET_EXPERIMENT_ID](ids) {
      this.ids = addExperiment(this.ids, ids);
    },
    [SET_EXPERIMENT_CLIENT_ID](clientIds) {
      this.clientIds = addExperiment(this.clientIds, clientIds);
    },
    [SETUP_SERVER_SIDE_EXPERIMENTATION]() {
      const pageData = usePageData();
      if (pageData.experimentation?.recipieIds) {
        this[SET_EXPERIMENT_ID](pageData.experimentation.recipieIds);
      }
    },
    [SETUP_CLIENT_SIDE_EXPERIMENTATION]() {
      const envProps = useEnvProps();
      const recipieIds = getClientRecipieIds();

      if (envProps.isNavSSR) {
        // append only defined in config ids with "clientSide:true" flag
        const clientIdsFromConfig = recipieIds.filter((id) => experiments.some(({ treatment, clientSide }) => treatment === id && clientSide));

        if (clientIdsFromConfig.length) {
          this[SET_EXPERIMENT_ID](clientIdsFromConfig);
        }
      } else {
        this[SET_EXPERIMENT_ID](recipieIds);
      }
      this[SET_EXPERIMENT_CLIENT_ID](recipieIds);
    },
  },
});
